import React, { useState } from 'react';

export interface ActionMenuItem {
  key: string;
  label: React.ReactNode;
  icon?: string | React.ReactNode;
  danger?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}

export interface MoreActionMenuProps {
  /** 触发按钮图标类名或 React 节点 (默认 'ri-more-fill text-base') */
  icon?: React.ReactNode;
  /** 触发按钮自定义 className */
  buttonClassName?: string;
  /** 触发按钮提示文字 */
  title?: string;
  /** 气泡菜单对齐方向 ('left' | 'right'，默认 'right') */
  align?: 'left' | 'right';
  /** 菜单项列表 */
  items: ActionMenuItem[];
  /** 菜单宽度及样式类名 */
  menuClassName?: string;
  /** 气泡 zIndex (默认 20) */
  zIndex?: number;
}

export default function MoreActionMenu({
  icon = <i className="ri-more-fill text-base"></i>,
  buttonClassName = 'p-1 rounded-lg hover:bg-gray-100 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer flex items-center justify-center',
  title = '更多操作',
  align = 'right',
  items,
  menuClassName = 'w-32',
  zIndex = 20,
}: MoreActionMenuProps) {
  const [open, setOpen] = useState(false);

  // 若没有有效的菜单项，则不渲染触发按钮
  if (!items || items.length === 0) {
    return null;
  }

  const renderItemIcon = (itemIcon?: React.ReactNode) => {
    if (!itemIcon) return null;
    if (typeof itemIcon === 'string') {
      return <i className={`${itemIcon} text-sm`}></i>;
    }
    return itemIcon;
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        className={buttonClassName}
        title={title}
      >
        {typeof icon === 'string' ? <i className={icon}></i> : icon}
      </button>

      {open && (
        <>
          {/* 透明遮罩，点击外部收起 */}
          <div
            className="fixed inset-0"
            style={{ zIndex: zIndex - 1 }}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          />
          {/* 气泡菜单 */}
          <div
            style={{ zIndex }}
            className={`absolute ${
              align === 'right' ? 'right-0' : 'left-0'
            } top-full mt-1 ${menuClassName} bg-white rounded-xl shadow-lg border border-background-200/80 py-1 text-xs animate-in fade-in zoom-in-95 duration-100`}
          >
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                disabled={item.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  item.onClick?.();
                }}
                className={`w-full px-3 py-2 text-left flex items-center gap-2 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
                  item.danger
                    ? 'hover:bg-red-50 text-red-600'
                    : 'hover:bg-gray-50 text-foreground-700 hover:text-foreground-950'
                }`}
              >
                {renderItemIcon(item.icon)}
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
